import React from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar } from 'recharts';
import type { DiretoriaIndicator } from '@/data/diretoriaTypes';
import { diretoriaSparklineStroke } from '@/lib/diretoriaKpiTone';

/** Prévia compacta no cartão alinhada ao tipo de gráfico do modal. */
export const DiretoriaSparkline: React.FC<{ indicator: DiretoriaIndicator }> = ({ indicator }) => {
  const kpis = Array.isArray(indicator?.kpis) ? indicator.kpis : [];
  const pk = Math.min(indicator?.primaryKpiIndex ?? 0, Math.max(0, kpis.length - 1));
  const stroke = diretoriaSparklineStroke(kpis[pk]?.tone);
  const h = 40;

  if (!indicator || indicator.chartKind === 'none') {
    return <div className="h-10 w-full rounded-md bg-muted/40" aria-hidden />;
  }

  if (indicator.chartKind === 'pie') {
    const pieRaw = indicator.pieData?.length
      ? indicator.pieData
      : (indicator.series ?? []).map((s) => ({ name: s?.name ?? '—', value: Number(s?.valor) || 0 }));
    const pie = (pieRaw ?? []).filter((p) => p && Number.isFinite(Number(p.value)));
    if (!pie.length) {
      return <div className="h-10 w-full rounded-md bg-muted/40" aria-hidden />;
    }
    const data = pie.slice(0, 10).map((p) => ({ name: String(p.name ?? '—'), valor: Number(p.value) || 0 }));
    return (
      <div className="w-full" style={{ height: h }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
            <Bar dataKey="valor" fill={stroke} radius={[2, 2, 0, 0]} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const series = Array.isArray(indicator.series) ? indicator.series.filter(Boolean) : [];
  if (!series.length) {
    return <div className="h-10 w-full rounded-md bg-muted/40" aria-hidden />;
  }

  const data = series.slice(-10).map((s) => ({
    name: String(s?.name ?? ''),
    valor: Number(s?.valor) || 0,
    valor2: s?.valor2 != null ? Number(s.valor2) || 0 : undefined,
  }));
  const useLine =
    indicator.chartKind === 'line' ||
    (indicator.chartKind === 'barGrouped' && data.length >= 4);

  if (useLine) {
    return (
      <div className="w-full" style={{ height: h }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 2, left: 0, bottom: 0 }}>
            <Line
              type="monotone"
              dataKey="valor"
              stroke={stroke}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
            {data.some((d) => d.valor2 != null) ? (
              <Line
                type="monotone"
                dataKey="valor2"
                stroke={stroke}
                strokeWidth={1}
                strokeOpacity={0.45}
                dot={false}
                isAnimationActive={false}
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const key = indicator.barSeriesKey === 'valor2' ? 'valor2' : 'valor';
  return (
    <div className="w-full" style={{ height: h }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 2, right: 2, left: 0, bottom: 0 }}>
          <Bar dataKey={key} fill={stroke} radius={[2, 2, 0, 0]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
